import { guideStorageKey } from '@dataroom/shared';
import type {
  DdPhase,
  Room,
  RoomChecklistFigures,
  RoomDocument,
  RoomSeller,
  TenantPlan,
} from '@dataroom/shared';

/**
 * The admin's first-run guides: the free trial's "See what ClosingVault does" and a new
 * subscriber's "Get your first deal running". Pure facts here (what the product's own
 * reads say is done); the panel is `components/FirstRunGuides.tsx`, the shared mechanics
 * `@dataroom/shared` first-run-guide.
 */

export const TRIAL_GUIDE_KEY = guideStorageKey('trial');
export const SUBSCRIBER_GUIDE_KEY = guideStorageKey('subscriber');

/** A subscription counts as new for this long after the firm entered its price cohort. */
export const NEW_SUBSCRIBER_DAYS = 30;

/** The id of the Rooms page's room-name input, which the guide's first step focuses. */
export const NEW_ROOM_NAME_INPUT_ID = 'new-room-name';

/** A paid plan (not the free trial, not an enterprise arrangement). */
export function isNewPaidSubscriber(plan: TenantPlan | undefined, now = Date.now()): boolean {
  if (!plan || plan.isTrial || plan.tier === 'free_trial') return false;
  const since = plan.priceCohortSince ? Date.parse(plan.priceCohortSince) : NaN;
  if (Number.isNaN(since)) return false;
  return now - since <= NEW_SUBSCRIBER_DAYS * 86_400_000;
}

const isActive = (r: Room) => r.lifecycleState === 'active' || r.lifecycleState === 'draft';

/** The trial's room: its first active room (a trial holds one). */
export function trialRoom(rooms: readonly Room[]): Room | undefined {
  return [...rooms]
    .filter(isActive)
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))[0];
}

/** A new subscriber's deal: their newest active room created since they subscribed. */
export function subscriberRoom(rooms: readonly Room[], plan: TenantPlan | undefined) {
  const since = plan?.priceCohortSince ? Date.parse(plan.priceCohortSince) : NaN;
  if (Number.isNaN(since)) return undefined;
  return [...rooms]
    .filter((r) => isActive(r) && Date.parse(r.createdAt) >= since)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
}

/** What the room's own reads say is done (each undefined while still loading → not done). */
export interface RoomFacts {
  checklist?: RoomChecklistFigures;
  buyers?: number;
  documents?: readonly RoomDocument[];
  sellers?: readonly RoomSeller[];
  phases?: readonly DdPhase[];
}

export interface TrialFacts {
  hasRoom: boolean;
  /** The room's checklist is set up (it has phases or items). */
  hasChecklist: boolean;
  hasDocuments: boolean;
  /** At least one checklist item is covered or signed "doesn't apply". */
  filed: boolean;
  /** A person moved a document or set its date (their own decision, not the AI's). */
  personEdited: boolean;
  /** At least one document has been through redaction. */
  redacted: boolean;
  sellerInvited: boolean;
  phaseReleased: boolean;
}

export function trialFacts(hasRoom: boolean, facts: RoomFacts): TrialFacts {
  const docs = facts.documents ?? [];
  return {
    hasRoom,
    hasChecklist: (facts.checklist?.total ?? 0) > 0 || (facts.phases?.length ?? 0) > 0,
    hasDocuments: docs.length > 0,
    filed: (facts.checklist?.resolved ?? 0) > 0,
    personEdited: docs.some(
      (d) => d.filedBy === 'person' || d.dataDateSetBy === 'person' || d.periodSetBy === 'person',
    ),
    redacted: docs.some((d) => d.redactionStatus === 'complete'),
    sellerInvited: (facts.sellers?.length ?? 0) > 0,
    phaseReleased: (facts.phases ?? []).some((p) => p.releasedAt !== null),
  };
}

export interface SubscriberFacts {
  /** A room created since subscribing, with its checklist set up. */
  roomReady: boolean;
  sellerInvited: boolean;
  phaseReleased: boolean;
  buyersInvited: boolean;
}

export function subscriberFacts(room: Room | undefined, facts: RoomFacts): SubscriberFacts {
  return {
    roomReady: room !== undefined && (facts.checklist?.total ?? 0) > 0,
    sellerInvited: (facts.sellers?.length ?? 0) > 0,
    phaseReleased: (facts.phases ?? []).some((p) => p.releasedAt !== null),
    buyersInvited: (facts.buyers ?? 0) > 0,
  };
}

/** The 1-minute walkthrough of the spreadsheet → categories → filed process, on the marketing site. */
export const CHECKLIST_IMPORT_VIDEO_PATH = '/media/checklist-import.mp4';

/** The marketing site's origin (the same `VITE_MARKETING_SITE_URL` the deploy sets), production by default. */
export function checklistImportVideoUrl(
  origin: string | undefined = import.meta.env.VITE_MARKETING_SITE_URL,
): string {
  return `${(origin || 'https://closingvault.app').replace(/\/+$/, '')}${CHECKLIST_IMPORT_VIDEO_PATH}`;
}
